import { useTranslation } from 'react-i18next';
import { displayName } from '../../utils/userLabel';
import { shortTime } from '../../utils/chat/messages';
import * as S from '../specimens/styles';

/*
 * My conversations, newest first (GET /chat/channels): each with its last
 * message, when, and how many are unread -- a muted one shows its count dimmed,
 * and never adds to the header's badge. `headingAs` is the page's h1 by
 * default; inside a sheet, whose own title is its h2, a lower one.
 */
export default function ChannelList({ channels, selected, onSelect, onNew, canStart, headingAs: Heading = 'h1' }) {
  const { t, i18n } = useTranslation(['chat', 'common']);
  return (
    <nav aria-label={t('conversations')} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: '1px solid var(--c-border)' }}>
        <Heading style={{ margin: 0, fontSize: 18, flex: 1 }}>{t('title')}</Heading>
        {canStart && (
          <button type="button" style={S.btn()} onClick={onNew} data-testid="chat-new-open" title={t('tip.new')}>
            {t('new')}
          </button>
        )}
      </div>
      {channels === null && <p style={{ ...S.hint, padding: 12 }}>{t('loading')}</p>}
      {channels && channels.length === 0 && <p style={{ ...S.hint, padding: 12 }}>{t('noConversations')}</p>}
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, overflowY: 'auto', flex: 1 }} data-testid="chat-channels">
        {(channels || []).map((c) => {
          const last = c.last_message;
          const active = c.channel_id === selected;
          return (
            <li key={c.channel_id}>
              <button type="button" onClick={() => onSelect(c.channel_id)} aria-current={active ? 'true' : undefined}
                data-testid="chat-channel" data-unread={c.unread}
                style={{ width: '100%', textAlign: 'start', border: 'none', borderBottom: '1px solid var(--c-border)',
                  padding: '10px 12px', cursor: 'pointer', font: 'inherit', color: 'var(--c-text-fg)',
                  background: active ? 'var(--c-primary-soft)' : 'transparent', display: 'flex', gap: 8 }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
                    <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} dir="auto">
                      {c.kind === 'dm' ? displayName(c.title, t) || t('someone') : c.title}
                    </strong>
                    {c.muted && <span aria-label={t('muted')} title={t('muted')}>🔕</span>}
                  </span>
                  <span style={{ ...S.hint, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    dir="auto">
                    {!last ? t('noMessages') : last.removed ? t('removed.short')
                      : last.kind === 'system' ? t('system.short')
                        : last.kind === 'media' ? mediaPreview(last, t) : last.preview}
                  </span>
                </span>
                <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                  <span style={S.hint}>{last ? shortTime(last.created_at, i18n.language) : ''}</span>
                  {c.unread > 0 && (
                    <span data-testid="chat-channel-unread" style={{ ...badge, opacity: c.muted ? 0.5 : 1 }}>
                      {c.unread > 99 ? '99+' : c.unread}
                    </span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/** A media message in the list: what it carries ("📷 Photo"), then its caption. */
function mediaPreview(last, t) {
  const kinds = (last.attachments || []).map((kind) => t(`attach.preview.${kind}`));
  return [kinds.join(' · '), last.preview].filter(Boolean).join(' · ') || t('attach.preview.media');
}

const badge = {
  background: 'var(--c-primary)', color: 'var(--c-on-accent)', borderRadius: 999, fontSize: 12, padding: '0 7px',
  minWidth: 20, textAlign: 'center',
};
